/**
 * Sprint M5.42 Block B (31.07.2026) — Vorab-Einblick-exklusiver Kurs
 * „Ruhe im Chaos".
 *
 * Inhalte kommen komplett aus dem Backend (ENV-driven):
 *   GET /api/courses/ruhe-im-chaos/content
 * Backend gated per `is_pionier`. Kein Pionier → 403 → Redirect.
 *
 * Vimeo-Embeds haengen programmatisch `?dnt=1` an (GDPR — kein DoNotTrack-
 * Tracking, Vimeo-Player laedt keine Analytics-Cookies).
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, FileText, Loader2, Lock, PlayCircle } from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";

// Simpler Markdown-Renderer: **bold** + Zeilenumbrueche. Genug fuer Intro.
function renderMarkdown(text) {
  if (!text) return null;
  const parts = text.split("\n").map((line, idx) => {
    // Bold-Regex: **text**
    const nodes = [];
    let cursor = 0;
    const re = /\*\*([^*]+)\*\*/g;
    let match;
    while ((match = re.exec(line)) !== null) {
      if (match.index > cursor) {
        nodes.push(line.slice(cursor, match.index));
      }
      nodes.push(
        <strong key={`b-${idx}-${match.index}`} className="font-bold">
          {match[1]}
        </strong>,
      );
      cursor = match.index + match[0].length;
    }
    if (cursor < line.length) nodes.push(line.slice(cursor));
    return (
      <p key={`p-${idx}`} className="text-base text-slate-700 leading-relaxed">
        {nodes}
      </p>
    );
  });
  return <div className="space-y-3">{parts}</div>;
}

/** Baut Vimeo-Embed-URL mit `?dnt=1` fuer GDPR. */
function buildVimeoUrl(vimeoId, hash) {
  if (!vimeoId) return null;
  const params = new URLSearchParams({
    dnt: "1",
    playsinline: "1",
    title: "0",
    byline: "0",
    portrait: "0",
  });
  if (hash) params.set("h", hash);
  return `https://player.vimeo.com/video/${vimeoId}?${params.toString()}`;
}

function VideoCard({ video }) {
  const url = buildVimeoUrl(video.vimeo_id, video.vimeo_hash);
  if (!url) {
    return (
      <div
        className="relative w-full rounded-xl overflow-hidden bg-slate-900 flex items-center justify-center text-center p-6"
        style={{ aspectRatio: "16 / 9" }}
        data-testid={`ric-video-locked-${video.key}`}
      >
        <div className="text-white">
          <Lock className="w-8 h-8 mx-auto mb-2 opacity-80" />
          <p className="text-sm font-semibold">Bald verfügbar</p>
          <p className="text-xs text-white/70 mt-1">{video.title}</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className="relative w-full rounded-xl overflow-hidden bg-black"
      style={{ aspectRatio: "16 / 9" }}
      data-testid={`ric-video-${video.key}`}
    >
      <iframe
        src={url}
        title={video.title}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        data-testid={`ric-video-iframe-${video.key}`}
      />
    </div>
  );
}

function PdfLink({ pdf }) {
  if (!pdf.url) {
    return (
      <div
        className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50 text-slate-500"
        data-testid={`ric-pdf-placeholder-${pdf.key}`}
      >
        <FileText className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-700">{pdf.label}</p>
          <p className="text-xs">Wird in Kürze freigeschaltet.</p>
        </div>
      </div>
    );
  }
  return (
    <a
      href={pdf.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#4563BB] hover:shadow-sm transition-all group"
      data-testid={`ric-pdf-link-${pdf.key}`}
    >
      <FileText className="w-5 h-5 text-[#4563BB] flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-[#4563BB]">
          {pdf.label}
        </p>
        <p className="text-xs text-slate-500">PDF herunterladen</p>
      </div>
    </a>
  );
}

export default function DashboardRuheImChaos() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ok | forbidden | error
  const [content, setContent] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/courses/ruhe-im-chaos/content")
      .then((res) => {
        if (cancelled) return;
        setContent(res.data);
        setState("ok");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setState("forbidden");
        } else if (err?.response?.status === 401) {
          navigate("/login", { replace: true });
        } else {
          setState("error");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (state === "loading") {
    return (
      <RedesignLayout>
        {() => (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[var(--fts-primary-bright)] animate-spin" />
          </div>
        )}
      </RedesignLayout>
    );
  }

  if (state === "forbidden") {
    return (
      <RedesignLayout>
        {() => (
          <div
            className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12"
            data-testid="ric-forbidden"
          >
            <div className="w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8 text-center">
              <div className="inline-flex w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 items-center justify-center mb-4">
                <Lock className="w-7 h-7 text-slate-500 dark:text-slate-400" />
              </div>
              <h1 className="text-xl font-extrabold text-slate-900 dark:text-white">
                Dieser Kurs ist derzeit noch nicht verfügbar.
              </h1>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                &bdquo;Ruhe im Chaos&ldquo; wird bald für alle freigeschaltet.
              </p>
              <Link
                to="/dashboard"
                className="inline-flex mt-6 px-5 py-2.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-semibold text-sm"
                data-testid="ric-goto-warteliste"
              >
                Zurück in den Mitgliederbereich →
              </Link>
            </div>
          </div>
        )}
      </RedesignLayout>
    );
  }

  if (state === "error" || !content) {
    return (
      <RedesignLayout>
        {() => (
          <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              Da ist gerade etwas schiefgelaufen.
            </h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Bitte versuche es in einem Moment noch einmal.
            </p>
            <Link
              to="/dashboard"
              className="inline-block mt-4 text-sm font-semibold text-[var(--fts-primary-bright)] hover:underline"
            >
              Zurück in den Mitgliederbereich
            </Link>
          </div>
        )}
      </RedesignLayout>
    );
  }

  return (
    <RedesignLayout>
      {() => (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10" data-testid="ric-page">
          <header className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
              Kurs · Pionier-Bonus
            </p>
            <h1
              className="mt-1 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight"
              data-testid="ric-title"
            >
              {content.title}
            </h1>
            {content.subtitle && (
              <p
                className="mt-2 text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-2xl"
                data-testid="ric-subtitle"
              >
                {content.subtitle}
              </p>
            )}
          </header>

          {content.markdown_intro && (
            <section
              className="mb-10 p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
              data-testid="ric-markdown-intro"
            >
              {renderMarkdown(content.markdown_intro)}
            </section>
          )}

          <section className="mb-10" data-testid="ric-videos-section">
            <div className="flex items-center gap-2 mb-4">
              <PlayCircle className="w-5 h-5 text-[var(--fts-primary-bright)]" />
              <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">
                4 Impulse zum Anschauen
              </h2>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
              data-testid="ric-videos-grid"
            >
              {(content.videos || []).map((video) => (
                <div key={video.key}>
                  <VideoCard video={video} />
                  <p className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    {video.position}. {video.title}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {(content.pdfs || []).length > 0 && (
            <section data-testid="ric-pdfs-section">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-5 h-5 text-[var(--fts-primary-bright)]" />
                <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">
                  Zum Mitnehmen
                </h2>
              </div>
              <div
                className="grid grid-cols-1 md:grid-cols-3 gap-3"
                data-testid="ric-pdfs-grid"
              >
                {content.pdfs.map((pdf) => (
                  <PdfLink key={pdf.key} pdf={pdf} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </RedesignLayout>
  );
}
